
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator as CalcIcon, Plus, Trash2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export const CombineLoanCalculator = () => {
  const [currentLoans, setCurrentLoans] = useState<{ amount: number; interest: number }[]>([
    { amount: 5000, interest: 12 },
    { amount: 3000, interest: 18 },
  ]);
  
  const [newLoanInterest, setNewLoanInterest] = useState(7.5);
  const [loanTerm, setLoanTerm] = useState(5);
  const [result, setResult] = useState<{
    originalMonthly: number;
    newMonthly: number;
    originalTotal: number;
    newTotal: number;
    savings: number;
  } | null>(null);

  const [loanSummary, setLoanSummary] = useState<{
    totalAmount: number;
    averageInterest: number;
  }>({ totalAmount: 0, averageInterest: 0 });

  // Update loan summary whenever loans change
  useEffect(() => {
    updateLoanSummary();
  }, [currentLoans]);

  const handleAddLoan = () => {
    setCurrentLoans([...currentLoans, { amount: 0, interest: 0 }]);
    toast.success("Uusi laina lisätty");
  };

  const handleRemoveLoan = (index: number) => {
    if (currentLoans.length > 1) {
      const newLoans = [...currentLoans];
      newLoans.splice(index, 1);
      setCurrentLoans(newLoans);
      toast.success("Laina poistettu");
    } else {
      toast.warning("Vähintään yksi laina täytyy säilyttää");
    }
  };

  const handleLoanChange = (index: number, field: 'amount' | 'interest', value: string) => {
    const newLoans = [...currentLoans];
    newLoans[index][field] = parseFloat(value) || 0;
    setCurrentLoans(newLoans);
  };

  const updateLoanSummary = () => {
    const totalAmount = currentLoans.reduce((sum, loan) => sum + (loan.amount || 0), 0);
    const weightedInterest = currentLoans.reduce((sum, loan) => {
      return sum + ((loan.amount || 0) * (loan.interest || 0));
    }, 0);
    const averageInterest = totalAmount > 0 ? weightedInterest / totalAmount : 0;

    setLoanSummary({
      totalAmount,
      averageInterest,
    });
  };

  const calculateSavings = () => {
    const totalLoanAmount = currentLoans.reduce((sum, loan) => sum + loan.amount, 0);
    
    if (totalLoanAmount <= 0) {
      toast.error("Syötä vähintään yksi laina laskeaksesi säästöt");
      return;
    }
    
    // Laske nykyisten lainojen kuukausierä ja kokonaiskustannukset (yksinkertaistettu laskenta)
    let originalMonthlyTotal = 0;
    let originalTotalPayment = 0;
    
    currentLoans.forEach(loan => {
      if (loan.amount > 0 && loan.interest > 0) {
        const monthlyInterest = loan.interest / 100 / 12;
        const months = loanTerm * 12;
        const monthlyPayment = (loan.amount * monthlyInterest * Math.pow(1 + monthlyInterest, months)) / (Math.pow(1 + monthlyInterest, months) - 1);
        
        originalMonthlyTotal += monthlyPayment;
        originalTotalPayment += monthlyPayment * months;
      }
    });
    
    // Laske uuden lainan kuukausierä ja kokonaiskustannukset
    const newMonthlyInterest = newLoanInterest / 100 / 12;
    const months = loanTerm * 12;
    const newMonthlyPayment = (totalLoanAmount * newMonthlyInterest * Math.pow(1 + newMonthlyInterest, months)) / (Math.pow(1 + newMonthlyInterest, months) - 1);
    const newTotalPayment = newMonthlyPayment * months;
    
    // Laske säästö
    const savings = originalTotalPayment - newTotalPayment;
    
    setResult({
      originalMonthly: originalMonthlyTotal,
      newMonthly: newMonthlyPayment,
      originalTotal: originalTotalPayment,
      newTotal: newTotalPayment,
      savings: savings
    });
    
    toast.success("Säästölaskelma valmis!");
  };

  return (
    <Card className="bg-white rounded-2xl shadow-xl border-0 w-full max-w-md">
      <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-t-2xl p-4">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 p-2 rounded-full">
            <CalcIcon className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Yhdistelylainan säästölaskuri</h3>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Lainat yhteensä</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <Label className="text-xs text-blue-600">Lainojen summa</Label>
                <p className="text-base font-bold text-blue-900">{loanSummary.totalAmount.toLocaleString()} €</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <Label className="text-xs text-blue-600">Keskikorko</Label>
                <p className="text-base font-bold text-blue-900">{loanSummary.averageInterest.toFixed(1)} %</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <Label className="text-sm font-medium text-gray-700 block">1. Listaa nykyiset lainasi</Label>
            {currentLoans.map((loan, index) => (
              <div key={index} className="grid grid-cols-7 sm:grid-cols-8 gap-2 items-center bg-white p-3 rounded-lg shadow-sm">
                <div className="col-span-3">
                  <Label htmlFor={`loan-amount-${index}`} className="text-xs text-gray-600">Lainan määrä (€)</Label>
                  <Input
                    id={`loan-amount-${index}`}
                    type="number"
                    value={loan.amount || ''}
                    onChange={(e) => handleLoanChange(index, 'amount', e.target.value)}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div className="col-span-3">
                  <Label htmlFor={`loan-interest-${index}`} className="text-xs text-gray-600">Korko (%)</Label>
                  <Input
                    id={`loan-interest-${index}`}
                    type="number"
                    value={loan.interest || ''}
                    onChange={(e) => handleLoanChange(index, 'interest', e.target.value)}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div className="col-span-1 sm:col-span-2 flex justify-end items-end">
                  <Button 
                    type="button" 
                    onClick={() => handleRemoveLoan(index)}
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 mt-1 text-gray-600 border-gray-300 hover:bg-gray-100 rounded-lg"
                    disabled={currentLoans.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              onClick={handleAddLoan}
              variant="outline"
              className="mt-2 w-full border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50 font-medium rounded-lg h-9"
            >
              <Plus className="h-4 w-4 mr-2" /> Lisää laina
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div>
              <Label htmlFor="new-interest" className="text-sm font-medium text-gray-700">2. Yhdistelylainan korko (%)</Label>
              <Input
                id="new-interest"
                type="number"
                value={newLoanInterest || ''}
                onChange={(e) => setNewLoanInterest(parseFloat(e.target.value) || 0)}
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label htmlFor="loan-term" className="text-sm font-medium text-gray-700">3. Laina-aika (vuotta)</Label>
              <Input
                id="loan-term"
                type="number"
                value={loanTerm || ''}
                onChange={(e) => setLoanTerm(parseInt(e.target.value) || 0)}
                className="mt-1 h-9"
              />
            </div>
          </div>

          <Button
            onClick={calculateSavings}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 rounded-xl py-2 font-semibold shadow-md hover:shadow-lg transition-all text-base"
          >
            <CalcIcon className="h-4 w-4 mr-2" /> Laske säästöt
          </Button>

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 shadow-md"
            >
              <h3 className="font-bold text-base text-green-700 mb-2">Laskelman tulos:</h3>
              
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div className="bg-white p-2 rounded-lg shadow-sm">
                  <p className="text-xs text-gray-600">Nykyinen kuukausierä:</p>
                  <p className="font-semibold text-gray-900 text-sm">{result.originalMonthly.toFixed(2)} €/kk</p>
                </div>
                <div className="bg-white p-2 rounded-lg shadow-sm">
                  <p className="text-xs text-gray-600">Uusi kuukausierä:</p>
                  <p className="font-semibold text-gray-900 text-sm">{result.newMonthly.toFixed(2)} €/kk</p>
                </div>
              </div>
              
              <div className="bg-white p-2 rounded-lg shadow-sm mb-2">
                <p className="text-xs text-gray-600">Säästö kuukaudessa:</p>
                <p className="font-medium text-green-600 text-sm">
                  {(result.originalMonthly - result.newMonthly).toFixed(2)} €/kk
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-green-100 to-green-50 p-3 rounded-lg shadow-sm mb-3">
                <p className="text-xs text-gray-700">Kokonaissäästö laina-aikana:</p>
                <p className="font-bold text-base text-green-600">
                  {result.savings.toFixed(2)} €
                </p>
              </div>
              
              <Link 
                to={`/kilpailuta?type=yhdistelylaina&amount=${loanSummary.totalAmount}&term=${loanTerm}`}
                className="block w-full bg-green-600 hover:bg-green-700 text-white text-center py-2 px-4 rounded-lg transition-colors duration-200 font-medium shadow-md hover:shadow-lg flex items-center justify-center text-sm"
              >
                Kilpailuta lainasi ja säästä <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
